import { type MenuItem } from '@/features/sidebar/types'
import {
  Item,
  User,
  Logo,
  NewChat,
  Version,
} from '@/features/sidebar/components'
import { useKeyboardShortcuts, useRecentChats } from '@/features/sidebar/hooks'
import rootStore from '@/app/store'
import cx from 'classnames'
import style from './index.module.css'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import {
  useState,
  useEffect,
  useRef,
  type MouseEvent,
  Dispatch,
  SetStateAction,
} from 'react'
import { Icon } from '@/ui/icon2'
import { motion, AnimatePresence } from 'framer-motion'

const MainNavigation = ({
  hideOnlyDesktopLinks,
  isCollapsed,
  setIsCollapsed,
}: {
  isCollapsed?: boolean
  setIsCollapsed?: Dispatch<SetStateAction<boolean>>
  hideOnlyDesktopLinks?: boolean
}) => {
  const { data: recentChats, refetch } = useRecentChats(!isCollapsed)

  useEffect(() => {
    if (!isCollapsed) {
      refetch()
    }
  }, [isCollapsed, refetch])

  const lastHistoryItems = () => {
    if (!recentChats || recentChats.length === 0) {
      return []
    }

    return recentChats.map((chat) => ({
      to: chat.url,
      label: chat.title,
    }))
  }

  const menu: MenuItem[] = [
    {
      to: '/explore',
      icon: 'explore',
      label: 'Explora',
      targetKey: 'sidebar.explore',
      dataScreenshotsId: 'menu-explore',
    },
    {
      to: '/projects',
      icon: 'archive',
      label: 'Proyectos',
      dataScreenshotsId: 'menu-projects',
    },
    {
      to: '/image',
      icon: 'image',
      label: 'Imagen',
      targetKey: 'sidebar.image',
      hiddenOnMobile: true,
      dataScreenshotsId: 'menu-image',
    },
    {
      to: '/use-cases',
      icon: 'resources',
      label: 'Casos de uso',
      targetKey: 'sidebar.useCases',
      dataScreenshotsId: 'menu-use-cases',
    },
    {
      to: '/agents',
      icon: 'agents',
      label: 'Agentes',
      hiddenOnMobile: true,
      dataScreenshotsId: 'menu-agents',
    },
    {
      to: 'https://aikit-taller.notion.site/',
      icon: 'book',
      label: 'Taller de IA',
      dataScreenshotsId: 'menu-taller-de-ia',
    },
    {
      to: '/history',
      icon: 'history',
      label: 'Historial',
      submenu: lastHistoryItems(),
      submenuTitle: 'Recientes',
      openByDefault: true,
      dataScreenshotsId: 'menu-history',
    },
  ]

  const filteredMenu = hideOnlyDesktopLinks
    ? menu.filter((item) => !('hiddenOnMobile' in item && item.hiddenOnMobile))
    : menu

  return (
    <nav className={style.menuContainer}>
      <div className={style.menuScroller}>
        <ul className={cx(style.menu, { [style.collapsed]: isCollapsed })}>
          {filteredMenu.map((item) => (
            <li key={item.label}>
              <Item
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
                item={item}
                dataScreenshotsId={item.dataScreenshotsId || ''}
              />
            </li>
          ))}
        </ul>
      </div>
    </nav>
  )
}

const FooterNavigation = ({
  isCollapsed,
  setIsCollapsed,
}: {
  isCollapsed?: boolean
  setIsCollapsed?: Dispatch<SetStateAction<boolean>>
}) => {
  const { auth } = rootStore

  const footerMenu: MenuItem[] = [
    {
      to: '/organization',
      icon: 'suitcase',
      label: 'Organización',
      dataScreenshotsId: 'menu-organization',
    },
    {
      to: '/knowledge-bases',
      icon: 'brain',
      label: 'Conocimiento',
      dataScreenshotsId: 'menu-knowledge-base',
    },
    {
      icon: 'grid',
      label: 'Módulos avanzados',
      popover: [
        {
          to: '/erp/invoicing',
          label: 'Facturación (Veri*Factu)',
          dataScreenshotsId: 'menu-advanced-modules-invoicing',
        },
        {
          to: '/erp/planner',
          label: 'Gestión de procesos',
          dataScreenshotsId: 'menu-advanced-modules-process-management',
        },
        // {
        //   to: '/erp/crm',
        //   label: 'Gestión de clientes',
        // },
        {
          to: '/erp/chat',
          label: 'Comunicación interna',
          dataScreenshotsId: 'menu-advanced-modules-internal-communication',
        },
        // {
        //   to: '/erp/calendar',
        //   label: 'Calendario',
        // },
        // {
        //   to: '/erp/documents',
        //   label: 'Documentos',
        // },
        // {
        //   to: '/erp/contacts',
        //   label: 'Contactos',
        // },
      ],
    },
  ]

  const isOrgManager = auth.user?.role === 'org-manager'

  return (
    <>
      {isOrgManager && (
        <ul
          className={cx(style.menu, style.footerMenu, {
            [style.collapsed]: isCollapsed,
          })}
        >
          {footerMenu.map((item, index) => (
            <li key={index}>
              <Item
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
                item={item}
                dataScreenshotsId={item.dataScreenshotsId || ''}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

const SidebarDesktop = ({
  isCollapsed,
  setIsCollapsed,
}: {
  isCollapsed: boolean
  setIsCollapsed?: Dispatch<SetStateAction<boolean>>
}) => {
  const handleCollapse = () => {
    setIsCollapsed?.(!isCollapsed)
  }

  return (
    <header className={cx(style.sidebar, { [style.isCollapsed]: isCollapsed })}>
      <div
        className={cx(style.sidebarHeader, { [style.collapsed]: isCollapsed })}
        data-screenshots-id="sidebar-header"
      >
        {!isCollapsed && <Logo />}
        <div className={style.sidebarCollapseButtonWrapper}>
          <button
            onClick={handleCollapse}
            aria-label={isCollapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
            className={style.sidebarCollapseButton}
          >
            <Icon size={14} name="sidebar" />
          </button>
        </div>
      </div>
      <NewChat isCollapsed={isCollapsed} />
      <MainNavigation
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
      />
      <div>
        <User isCollapsed={isCollapsed} narrow={isCollapsed} />
        <div className={style.footerMenuContainer}>
          <FooterNavigation
            isCollapsed={isCollapsed}
            setIsCollapsed={setIsCollapsed}
          />
          <Version />
        </div>
      </div>
    </header>
  )
}

const SidebarMobile = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)

  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsMenuOpen((prev) => !prev)
  }

  const handleContentClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    if (
      target.tagName === 'A' ||
      target.closest('a') ||
      target.id === 'new-chat'
    ) {
      setIsMenuOpen(false)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const iconName = isMenuOpen ? 'cross' : 'menu'

  return (
    <header
      className={cx(style.sidebarMobile, { [style.isMenuOpen]: isMenuOpen })}
    >
      <div>
        <Logo />
      </div>
      <User narrow hideOnlyDesktopLinks />

      <button
        className={style.menuButton}
        onClick={handleMenuClick}
        aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
        aria-expanded={isMenuOpen}
      >
        <Icon name={iconName} />
      </button>

      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              ref={sidebarRef}
              className={style.sidebarMobileContent}
              onClick={handleContentClick}
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{
                duration: 0.2,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              <NewChat />
              <MainNavigation hideOnlyDesktopLinks />
              <div className={style.footerMenuContainer}>
                <FooterNavigation />
                <Version />
              </div>
            </motion.div>

            <motion.div
              className={style.sidebarMobileOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.2,
                ease: [0.4, 0, 0.2, 1],
              }}
            />
          </>
        )}
      </AnimatePresence>
    </header>
  )
}

export const Sidebar = ({
  isCollapsed,
  setIsCollapsed,
}: {
  isCollapsed: boolean
  setIsCollapsed: Dispatch<SetStateAction<boolean>>
}) => {
  useKeyboardShortcuts()

  const isMobile = useMediaQuery('(max-width:900px)')

  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(false)
    }
  }, [isMobile])

  return isMobile ? (
    <SidebarMobile />
  ) : (
    <SidebarDesktop isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
  )
}
